import React from 'react';
import UserCard from './UserCard';
import { EyeSlashIcon } from '@heroicons/react/24/outline';

/**
 * HiddenUsersSection component - Displays hidden users for admin users only
 * Shows a dedicated section with hidden user count and appropriate styling
 */
const HiddenUsersSection = ({ 
  hiddenUsers = [], 
  isAdmin = false, 
  onUnhideUser = null, 
  loading = false 
}) => {
  // Don't render anything if not admin
  if (!isAdmin) {
    return null;
  }

  return (
    <div className="mt-12 pt-8 border-t-2 border-gray-200 dark:border-gray-700">
      {/* Section Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
            <EyeSlashIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Hidden Users
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {hiddenUsers.length} {hiddenUsers.length === 1 ? 'user' : 'users'} currently hidden from the directory
            </p>
          </div>
        </div>
        
        {/* Hidden count badge */}
        <div className="bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 px-4 py-2 rounded-full font-semibold">
          {hiddenUsers.length}
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="text-center py-8">
          <div className="inline-flex items-center space-x-2 text-gray-500 dark:text-gray-400">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600 dark:border-red-400"></div>
            <span className="text-sm font-medium">Loading hidden users...</span>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!loading && hiddenUsers.length === 0 && (
        <div className="text-center py-12">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8">
            <div className="flex justify-center mb-4">
              <div className="p-4 bg-gray-100 dark:bg-gray-700/50 rounded-full">
                <EyeSlashIcon className="h-8 w-8 text-gray-400 dark:text-gray-500" />
              </div>
            </div>
            
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              No Hidden Users
            </h3>
            
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              No users are currently hidden from the network directory.
            </p>
          </div>
        </div>
      )}

      {/* Hidden users grid */}
      {!loading && hiddenUsers.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 
                       gap-4 sm:gap-6 lg:gap-8 auto-rows-fr">
          {hiddenUsers.map((user) => (
            <UserCard
              key={user.uid || user.id}
              user={user}
              isAdmin={isAdmin}
              onUnhideUser={onUnhideUser}
              isHidden={true}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default HiddenUsersSection; 